import { sanitizeSurveyHtml } from '../../flow/sanitizeHtml';
import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import {
  DEFAULT_APRUV_TIP_TERMS_HTML,
  DEFAULT_APRUV_TIP_TERMS_TEXT,
  DEFAULT_APRUV_TIP_TERMS_LINK_LABEL,
  DEFAULT_APRUV_TIP_TERMS_MODAL_TITLE,
} from '../../legal/apruvTerms';

type ApruvTermsNoticeProps = {
  closeLabel?: string;
  closeAccessibleLabel?: string;
  linkLabel?: string | null;
  linkColor?: string;
  linkDecoration?: 'underline' | 'none';
  trailingPunctuation?: string;
  modalTitle?: string | null;
  text?: string | null;
  textAlign?: 'left' | 'center' | 'right';
  termsHtml?: string | null;
};

const ApruvTermsNotice: React.FC<ApruvTermsNoticeProps> = ({
  closeLabel = 'Close',
  closeAccessibleLabel = 'Close terms',
  linkLabel,
  linkColor = 'var(--cx-brand-primary, #0EA5E9)',
  linkDecoration = 'underline',
  trailingPunctuation = '.',
  modalTitle,
  text,
  textAlign = 'center',
  termsHtml,
}) => {
  const [open, setOpen] = React.useState(false);
  const resolvedLinkLabel = linkLabel?.trim() || DEFAULT_APRUV_TIP_TERMS_LINK_LABEL;
  const resolvedModalTitle = modalTitle?.trim() || DEFAULT_APRUV_TIP_TERMS_MODAL_TITLE;
  const resolvedText = text?.trim() || DEFAULT_APRUV_TIP_TERMS_TEXT;
  const resolvedTermsHtml = termsHtml?.trim() || DEFAULT_APRUV_TIP_TERMS_HTML;

  return (
    <>
      <Typography
        component="p"
        variant="caption"
        sx={{
          color: 'var(--cx-brand-text-muted, #667085)',
          lineHeight: 1.55,
          textAlign,
        }}
      >
        {resolvedText}{' '}
        <Box
          component="button"
          onClick={() => setOpen(true)}
          sx={{
            appearance: 'none',
            background: 'none',
            border: 0,
            color: linkColor,
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 900,
            p: 0,
            textDecoration: linkDecoration,
          }}
          type="button"
        >
          {resolvedLinkLabel}
        </Box>
        {trailingPunctuation}
      </Typography>
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => setOpen(false)}
        open={open}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'var(--cx-brand-surface, #FFFFFF)',
              borderRadius: 3,
              color: 'var(--cx-brand-text, #111827)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            alignItems: 'center',
            color: 'inherit',
            display: 'flex',
            gap: 1.5,
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: 'inherit', fontWeight: 900 }}>
            {resolvedModalTitle}
          </Typography>
          <IconButton
            aria-label={closeAccessibleLabel}
            onClick={() => setOpen(false)}
            size="small"
            sx={{ color: 'var(--cx-brand-text-muted, #667085)' }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'var(--cx-brand-border, #E2E8F0)' }}>
          <Typography
            component="div"
            dangerouslySetInnerHTML={{ __html: sanitizeSurveyHtml(resolvedTermsHtml) }}
            sx={{
              color: 'var(--cx-brand-text, #111827)',
              lineHeight: 1.65,
              '& a': { color: 'inherit', fontWeight: 800, textDecoration: 'underline' },
              '& b, & strong': { fontWeight: 900 },
              '& li': { pl: 0.25 },
              '& ol, & ul': { my: 0.5, pl: 2.25 },
              '& p, & h1, & h2, & h3, & h4, & h5, & h6': { color: 'inherit' },
              '& ul': { listStyleType: 'disc' },
              '& ol': { listStyleType: 'decimal' },
              '& p': { m: 0 },
              '& p + p': { mt: 1.1 },
            }}
            variant="body2"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            sx={{ color: linkColor, fontWeight: 900 }}
          >
            {closeLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApruvTermsNotice;
